/**
 * Per-signature burst cap for the Datadog log TRANSMIT path.
 *
 * A single client in a tight loop bills us for an unbounded number of lines
 * and buries every other device in the same query. One desktop machine stuck
 * on an old version produced 1.68M logs in a month on its own, and accounted
 * for 67% of the fleet-wide `IPC custom protocol failed` volume. Datadog bills
 * on ingest, so there is no ceiling that stops this on its own.
 *
 * Each distinct signature gets a small budget per fixed window. The leading
 * occurrences always survive, so a loop stays diagnosable and we can still see
 * WHICH device is looping; only the tail is dropped. Suppression reporting is
 * best-effort: the count rides out on the first kept line of the signature's
 * next window, and is lost if the signature is LRU-evicted first or never
 * recurs.
 *
 * This is the log pipe. `src/utils/rumErrorBurstThrottle.ts` is the same idea
 * on the RUM error pipe; the two are deliberately separate because they cap
 * different transports and neither one covers the other's traffic.
 */

/** Lines kept per signature per window. */
const BURST_LIMIT = 10;

const WINDOW_MS = 60_000;

/** Signatures tracked before the least-recently-seen is evicted. */
const MAX_TRACKED_SIGNATURES = 100;

const MAX_SIGNATURE_LENGTH = 200;

type Bucket = { windowStart: number; seen: number };

const buckets = new Map<string, Bucket>();

/**
 * Identifiers a looping call site varies on every iteration. Without this the
 * throttle is a no-op against the traffic that motivated it: Tauri's own
 * warning interpolates the callback id (`Couldn't find callback id ${id}. This
 * might happen when the app is reloaded while Rust is running an asynchronous
 * operation.`), so 145.9M verbatim-identical-looking lines are 145.9M distinct
 * strings, each landing in its own bucket and never reaching the cap.
 *
 * Order matters: UUIDs are matched before the digit run, or the digits inside
 * one would be collapsed first and leave a still-varying husk behind.
 *
 * Only the two shapes we actually emit are here. A bare hex id (a 16+ char
 * digest, no dashes) is NOT collapsed — no logging call site was found that
 * produces one, and a branch matching a shape nothing emits is an over-match
 * running on every line the app logs, not free insurance. A loop varying only
 * a bare hex id would therefore evade the cap and fall through to the LRU
 * bound below; add the branch when a real line shows up, with that line as the
 * test fixture.
 */
const VOLATILE_PATTERNS: ReadonlyArray<readonly [RegExp, string]> = [
  [/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '<uuid>'],
  [/\d+/g, '<n>'],
];

/**
 * The text a signature is built from.
 *
 * An Error is the case that matters. It is transmitted as its bare
 * classification (`TypeError`, `AxiosError ERR_NETWORK …`), so signing the
 * transmitted string would collapse most `logger.error(err)` call sites in
 * the app (37 files today) onto a handful of signatures. Ten unrelated errors
 * in a minute would then silently drop every error after them, fleet-wide. So
 * Errors are signed by their identity instead.
 *
 * This deliberately differs from what is transmitted (see ./README.md). The
 * signature only has to distinguish lines; it is a Map key and never leaves
 * the device.
 */
const signatureTextOf = (message: unknown): string => {
  if (typeof message === 'string') return message;
  if (message instanceof Error) return `${message.name}: ${message.message}`;
  if (message === null) return 'null';
  if (message === undefined) return 'undefined';

  try {
    return JSON.stringify(message) ?? '[unserializable]';
  } catch {
    return '[unserializable]';
  }
};

/**
 * Truncates BEFORE the patterns run. This is the hot path — it executes on
 * every transmitted line, ahead of the cap that makes the path cheap — and the
 * input can be a large `JSON.stringify`, so regex cost would otherwise scale
 * with payload size. Normalising can lengthen the text (`1` becomes `<n>`),
 * hence the second truncation. Each part gets its own budget, so a long
 * message cannot push the payload out of the signature entirely.
 */
const normalizePart = (text: string): string => {
  const raw = text.slice(0, MAX_SIGNATURE_LENGTH);

  const normalized = VOLATILE_PATTERNS.reduce(
    (acc, [pattern, placeholder]) => acc.replace(pattern, placeholder),
    raw
  );

  return normalized.slice(0, MAX_SIGNATURE_LENGTH);
};

/**
 * Groups the lines a loop repeats apart from an embedded identifier. The level
 * is part of the signature, so the same text logged at `warn` and at `error`
 * keeps independent budgets and one cannot silence the other.
 *
 * `additionalArgs` — the payload a call site passes after the message — is
 * part of the signature too, and it has to be. Under structured logging the
 * message is a CONSTANT and the varying part lives entirely in the payload:
 *
 *   logger.warn('[integrationAuth] legacy storage migration seeded nothing',
 *               { id, legacyKey, targetKey })
 *
 * That site fires once per migrated key. Signing on the message alone would
 * make all twelve one signature and silently report only the first ten, which
 * is the exact data loss the telemetry exists to detect. A loop is still
 * capped, because a payload that varies only by a counter or an id normalises
 * to the same text.
 *
 * Ambient context and `child()` bindings are deliberately NOT included. An
 * enricher may return per-call values (an active route, a session id), and a
 * signature keyed on those would give a single looping call site a fresh
 * budget on every iteration — the failure this cap exists to prevent.
 */
export const buildLogSignature = (
  level: string,
  message: unknown,
  additionalArgs?: unknown
): string => {
  const base = `${level}|${normalizePart(signatureTextOf(message))}`;

  return additionalArgs === undefined
    ? base
    : `${base}|${normalizePart(signatureTextOf(additionalArgs))}`;
};

export type LogBurstVerdict = {
  /** False when this signature is over budget for the current window. */
  keep: boolean;
  /**
   * Lines suppressed in this signature's last closed window. Best-effort:
   * lost on LRU eviction, and the window may have closed long ago.
   */
  droppedInPreviousWindow: number;
};

/**
 * Records one line against its signature's budget and reports whether it may
 * be transmitted. `now` is injected so callers (and tests) control the clock.
 */
export const admitLogLine = (signature: string, now: number): LogBurstVerdict => {
  const bucket = buckets.get(signature);
  const elapsed = bucket ? now - bucket.windowStart : 0;

  // A negative elapsed means the wall clock stepped backwards (NTP correction,
  // manual change); treat the window as expired so an over-budget signature
  // cannot freeze in the dropping state until the clock catches up.
  if (bucket && elapsed >= 0 && elapsed < WINDOW_MS) {
    bucket.seen += 1;
    // Re-insert so Map iteration order stays least-recently-seen first.
    buckets.delete(signature);
    buckets.set(signature, bucket);
    return { keep: bucket.seen <= BURST_LIMIT, droppedInPreviousWindow: 0 };
  }

  buckets.delete(signature);
  while (buckets.size >= MAX_TRACKED_SIGNATURES) {
    const oldest = buckets.keys().next().value;
    if (oldest === undefined) break;
    buckets.delete(oldest);
  }
  buckets.set(signature, { windowStart: now, seen: 1 });

  return {
    keep: true,
    droppedInPreviousWindow: bucket ? Math.max(0, bucket.seen - BURST_LIMIT) : 0,
  };
};

/** Test-only: clears every budget between cases. */
export const __resetLogBurstThrottleForTests = (): void => {
  buckets.clear();
};
